import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { registerRequest } from '@/lib/api';

type Role = 'vendor' | 'organizer';
type VendorProofImage = { id: string; file: File; url: string };

const roleData = {
  vendor: {
    label: 'Vendeur',
    icon: 'ri-store-2-line',
    color: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-400',
    text: 'text-emerald-700',
    desc: 'Vendez vos produits à des milliers de clients sur Foi Senegal.',
    badge: 'bg-emerald-100 text-emerald-700',
  },
  organizer: {
    label: 'Organisateur',
    icon: 'ri-calendar-event-line',
    color: 'from-gold to-gold-dark',
    bg: 'bg-gold-100',
    border: 'border-gold',
    text: 'text-gold-dark',
    desc: 'Créez et gérez vos événements, vendez vos tickets en ligne.',
    badge: 'bg-gold-100 text-gold-dark',
  },
};

export default function Register() {
  const navigate = useNavigate();
  const logoSrc = 'https://static.readdy.ai/image/7e0624576d141773c913eba1868f652c/ea02af35c9a274d0014fddb0274b3cab.png';
  const [searchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [vendorProofImages, setVendorProofImages] = useState<VendorProofImage[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  useEffect(() => {
    const role = searchParams.get('role') as Role | null;
    if (role === 'vendor' || role === 'organizer') {
      setSelectedRole(role);
    }
  }, [searchParams]);

  const currentRole = selectedRole ? roleData[selectedRole] : null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedRole) {
      setError('Veuillez sélectionner un rôle.');
      return;
    }
    if (selectedRole === 'vendor' && (vendorProofImages.length < 2 || vendorProofImages.length > 3)) {
      setError('Ajoutez 2 à 3 images de produits pour renforcer votre dossier.');
      return;
    }
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('La confirmation du mot de passe ne correspond pas.');
      return;
    }
    setLoading(true);
    setError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const firstName = String(formData.get('firstName') ?? '').trim();
      const lastName = String(formData.get('lastName') ?? '').trim();
      const city = String(formData.get('city') ?? '').trim();
      const country = String(formData.get('country') ?? '').trim();
      const structureName = String(
        selectedRole === 'vendor'
          ? (formData.get('shopName') ?? '')
          : (formData.get('orgName') ?? '')
      ).trim();
      const location = [city, country].filter(Boolean).join(', ');

      const payload = new FormData();
      payload.append('name', `${firstName} ${lastName}`.trim());
      payload.append('email', String(formData.get('email') ?? '').trim());
      payload.append('phone', String(formData.get('phone') ?? '').trim());
      payload.append('paroisse', location);
      payload.append('diocese_prefere', country);
      payload.append('structure_name', structureName);
      payload.append('role', selectedRole);
      payload.append('password', password);
      payload.append('password_confirmation', passwordConfirmation);
      payload.append('bio', String(formData.get('bio') ?? '').trim());

      if (selectedRole === 'vendor') {
        payload.append('category', String(formData.get('category') ?? '').trim());
        vendorProofImages.forEach((image) => payload.append('product_proof_images[]', image.file));
      } else {
        payload.append('category', String(formData.get('eventType') ?? '').trim());
      }

      await registerRequest(payload);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible d'envoyer la demande. Vérifiez votre connexion.");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream p-4 sm:p-6">
        <div className="w-full max-w-lg rounded-3xl border border-gray-100 bg-white p-6 text-center sm:p-12">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gold-100">
            <i className="ri-checkbox-circle-line text-4xl text-gold"></i>
          </div>
          <h2 className="mb-3 text-2xl font-bold text-navy">Demande envoyée !</h2>
          <p className="mb-2 text-sm leading-relaxed text-gray-500">
            Votre demande d'inscription en tant que <strong className={currentRole?.text}>{currentRole?.label}</strong> a bien été reçue.
          </p>
          <p className="mb-8 text-sm leading-relaxed text-gray-500">
            Notre équipe va examiner votre profil et vous contacter par email sous <strong>24 à 48 heures</strong> pour valider ou rejeter votre inscription.
          </p>
          <div className="mb-8 flex items-start gap-3 rounded-xl bg-gold-100 p-4 text-left">
            <i className="ri-time-line flex-shrink-0 text-gold-dark"></i>
            <p className="text-sm font-medium text-gold-dark">En attendant, vous pouvez vous connecter dès que l'admin aura validé votre compte.</p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full cursor-pointer whitespace-nowrap rounded-xl bg-navy py-3.5 text-sm font-bold text-white transition-all hover:bg-navy-light"
            >
              Aller à la connexion
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full cursor-pointer whitespace-nowrap rounded-xl border-2 border-gray-200 py-3 text-sm font-semibold text-gray-600 transition-all hover:border-navy hover:text-navy"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8F9FC] p-4 sm:p-6 lg:p-10">
      <div className="w-full max-w-6xl overflow-hidden rounded-[32px] border border-white/60 bg-white shadow-2xl shadow-navy/10">
        <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
          <div className="relative hidden min-h-[260px] flex-col overflow-hidden lg:flex lg:min-h-[760px]">
            <img
              src="https://readdy.ai/api/search-image?query=african%20entrepreneurs%20working%20digital%20platform%20laptop%20modern%20office%20vibrant%20startup%20teamwork%20energy%20success%20growth%20technology%20community%20warm%20colors%20professional&width=900&height=1100&seq=reg-bg-01&orientation=portrait"
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-navy/85 via-navy/70 to-navy/50"></div>
            <div className="relative z-10 flex h-full flex-col p-10 xl:p-14">
              <button onClick={() => navigate('/')} className="group flex w-fit cursor-pointer items-center gap-3">
                <img src={logoSrc} alt="Foi Senegal" className="h-10 w-10 rounded-xl bg-white object-contain" />
                <span className="text-lg font-bold text-white">Foi Senegal</span>
              </button>

              <div className="flex flex-1 flex-col justify-center">
                <div className="mb-6 h-1 w-12 rounded-full bg-gold"></div>
                <h2 className="mb-4 text-4xl font-bold leading-tight text-white">
                  Rejoignez
                  <br />
                  <span className="text-gold">l'aventure</span>
                  <br />
                  Foi Senegal
                </h2>
                <p className="mb-8 text-base leading-relaxed text-white/65">
                Faites grandir votre activité avec foi et détermination.
                Soyez parmi les premiers entrepreneurs chrétiens à rejoindre la plateforme et développez votre activité avec foi.
                </p>

                <div className="space-y-4">
                  {[
                    { icon: 'ri-shield-check-line', color: 'text-emerald-400', title: 'Inscription vérifiée', desc: 'Chaque compte est validé manuellement par notre équipe.' },
                    { icon: 'ri-rocket-2-line', color: 'text-gold', title: 'Démarrage rapide', desc: 'Votre compte est activé sous 24-48h après validation.' },
                    { icon: 'ri-group-line', color: 'text-teal-400', title: 'Communauté active', desc: 'Vendeurs et organisateurs chrétiens se rassemblent pour entreprendre avec Dieu au centre.' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white/10">
                        <i className={`${item.icon} ${item.color} text-base`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{item.title}</p>
                        <p className="mt-0.5 text-xs text-white/50">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col bg-[#F8F9FC]">
            <div className="flex flex-col gap-3 bg-navy p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6 lg:hidden">
              <button onClick={() => navigate('/')} className="flex cursor-pointer items-center gap-2">
                <img src={logoSrc} alt="Foi Senegal" className="h-8 w-8 rounded-lg bg-white object-contain" />
                <span className="font-bold text-white">Foi Senegal</span>
              </button>
              <button onClick={() => navigate('/login')} className="cursor-pointer text-left text-sm text-white/70 hover:text-white sm:text-right">
                Déjà inscrit ? Se connecter
              </button>
            </div>

            <div className="flex flex-1 items-center justify-center p-4 sm:p-8 xl:p-10">
              <div className="w-full max-w-lg rounded-[28px] border border-white/70 bg-white p-4 shadow-[0_24px_70px_rgba(27,42,74,0.12)] sm:p-8 xl:p-10">
                <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <button onClick={() => navigate('/')} className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-navy">
                    <i className="ri-arrow-left-line"></i>
                    Retour à l'accueil
                  </button>
                  <Link to="/login" className="text-sm font-semibold text-gold-dark transition hover:underline">
                    Aller à la connexion
                  </Link>
                </div>

                <div className="mb-8">
                  <h1 className="mb-1 text-2xl font-bold text-navy">Créez votre compte</h1>
                  <p className="text-sm text-gray-400">Faites votre premier pas sur Foi Senegal - inscription gratuite</p>
                </div>

                <div className="mb-6">
                  <p className="mb-3 text-xs font-bold uppercase tracking-widest text-navy/50">Étape 1 - Choisissez votre rôle</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {(Object.entries(roleData) as [Role, typeof roleData.vendor][]).map(([key, role]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setSelectedRole(key);
                          setError('');
                        }}
                        className={`flex cursor-pointer flex-col items-center rounded-xl border-2 p-4 transition-all ${
                          selectedRole === key ? `${role.border} ${role.bg}` : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className={`mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${role.color}`}>
                          <i className={`${role.icon} text-lg text-white`}></i>
                        </div>
                        <span className={`text-sm font-bold ${selectedRole === key ? role.text : 'text-navy'}`}>{role.label}</span>
                        <span className="mt-1 text-center text-xs leading-tight text-gray-400">{role.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className={`transition-all duration-300 ${selectedRole ? 'opacity-100' : 'pointer-events-none opacity-50'}`}>
                  <p className="mb-3 text-xs font-bold uppercase tracking-widest text-navy/50">Étape 2 - Vos informations</p>

                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Prénom *</label>
                        <input
                          name="firstName"
                          type="text"
                          required
                          placeholder="Jean-Pierre"
                          className="w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Nom *</label>
                        <input
                          name="lastName"
                          type="text"
                          required
                          placeholder="Coly"
                          className="w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                        />
                      </div>
                    </div>
                
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-navy">Adresse email *</label>
                      <div className="relative">
                        <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400"></i>
                        <input
                          name="email"
                          type="email"
                          required
                          placeholder="vous@email.com"
                          className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Mot de passe *</label>
                        <div className="relative">
                          <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400"></i>
                          <input
                            type={showPasswords ? 'text' : 'password'}
                            required
                            minLength={8}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="8 caractères minimum"
                            className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-10 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords((value) => !value)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy"
                          >
                            <i className={showPasswords ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Confirmer mot de passe *</label>
                        <div className="relative">
                          <i className="ri-lock-password-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400"></i>
                          <input
                            type={showPasswords ? 'text' : 'password'}
                            required
                            minLength={8}
                            value={passwordConfirmation}
                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                            placeholder="Retapez votre mot de passe"
                            className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-10 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords((value) => !value)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy"
                          >
                            <i className={showPasswords ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                
                    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-navy">
                      <input
                        type="checkbox"
                        checked={showPasswords}
                        onChange={(e) => setShowPasswords(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-navy focus:ring-gold/40"
                      />
                      <span>Afficher le mot de passe saisi</span>
                    </label>
                
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-navy">Numéro de téléphone *</label>
                      <div className="relative">
                        <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400"></i>
                        <input
                          name="phone"
                          type="tel"
                          required
                          placeholder="+221 77 699 00 00"
                          className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                        />
                      </div>
                    </div>
                
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Ville *</label>
                        <input
                          name="city"
                          type="text"
                          required
                          placeholder="Dakar"
                          className="w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-navy">Pays *</label>
                        <select
                          name="country"
                          required
                          className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                        >
                          <option value="">Sélectionner...</option>
                          <option>Sénégal</option>
                        </select>
                      </div>
                    </div>
                
                    {selectedRole === 'vendor' && (
                      <>
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-navy">Nom de votre boutique *</label>
                          <input
                            name="shopName"
                            type="text"
                            required
                            placeholder="Ma Boutique Wax"
                            className="w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-navy">Catégorie principale</label>
                          <select
                            name="category"
                            className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                          >
                            <option value="">Proposition (vendeur)</option>
                            <option>Livres & Bibles</option>
                            <option>Articles religieux (chapelets, croix, etc.)</option>
                            <option>Mode chrétienne</option>
                            <option>Bien-être</option>
                            <option>Artisanat</option>
                            <option>Papeterie (cahiers, carnets, supports)</option>
                            <option>Autre</option>
                          </select>
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-navy">
                            Ajoutez 2 à 3 images de vos produits *
                          </label>
                          <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-emerald-300 bg-emerald-50/70 px-4 py-6 text-center transition hover:border-emerald-400 hover:bg-emerald-50">
                            <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                              <i className="ri-image-add-line text-lg"></i>
                            </div>
                            <p className="text-sm font-semibold text-navy">Importer des photos de produits</p>
                            <p className="mt-1 text-xs text-gray-500">
                              JPG, PNG ou WEBP, jusqu&apos;à 3 fichiers pour donner plus de crédibilité à votre demande.
                            </p>
                            <input
                              name="productProofImages"
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              onChange={(e) => {
                                const nextFiles = Array.from(e.target.files ?? []);
                                setVendorProofImages((current) => {
                                  const merged = [...current];

                                  nextFiles.forEach((file) => {
                                    const alreadyAdded = merged.some(
                                      (item) =>
                                        item.file.name === file.name &&
                                        item.file.size === file.size &&
                                        item.file.lastModified === file.lastModified
                                    );

                                    if (!alreadyAdded && merged.length < 3) {
                                      merged.push({
                                        id: `${file.name}-${file.size}-${file.lastModified}`,
                                        file,
                                        url: URL.createObjectURL(file),
                                      });
                                    }
                                  });

                                  return merged;
                                });
                                setError('');
                                e.currentTarget.value = '';
                              }}
                            />
                          </label>
                          <p className="mt-2 text-xs text-gray-400">
                            Minimum 2 images, maximum 3.
                          </p>
                          {vendorProofImages.length > 0 && (
                            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                              {vendorProofImages.map((image, index) => (
                                <div key={image.id} className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                                  <button
                                    type="button"
                                    onClick={() => setPreviewImage(image.url)}
                                    className="block h-24 w-full"
                                  >
                                    <img src={image.url} alt={`Produit ${index + 1}`} className="h-full w-full object-cover" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setVendorProofImages((current) => {
                                        const next = current.filter((item) => item.id !== image.id);
                                        URL.revokeObjectURL(image.url);
                                        return next;
                                      });
                                      if (previewImage === image.url) {
                                        setPreviewImage(null);
                                      }
                                    }}
                                    className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white text-red-500 shadow-sm hover:bg-red-50"
                                    title="Supprimer cette image"
                                  >
                                    <i className="ri-delete-bin-line text-sm"></i>
                                  </button>
                                  <div className="px-3 py-2">
                                    <p className="truncate text-xs font-medium text-navy">{image.file.name}</p>
                                    <p className="text-[11px] text-gray-400">{Math.round(image.file.size / 1024)} Ko</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {previewImage ? (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setPreviewImage(null)}>
                              <div className="relative max-h-[90vh] w-full max-w-3xl" onClick={(event) => event.stopPropagation()}>
                                <button
                                  type="button"
                                  onClick={() => setPreviewImage(null)}
                                  className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy shadow-lg hover:bg-white"
                                >
                                  <i className="ri-close-line text-xl"></i>
                                </button>
                                <img src={previewImage} alt="Aperçu produit" className="max-h-[86vh] w-full rounded-3xl object-contain shadow-2xl" />
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </>
                    )}
                
                    {selectedRole === 'organizer' && (
                      <>
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-navy">Nom de votre organisation *</label>
                          <input
                            name="orgName"
                            type="text"
                            required
                            placeholder="Foi Events Senegal"
                            className="w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-navy">Type d'événements organisés</label>
                          <select
                            name="eventType"
                            className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                          >
                            <option value="">Proposition</option>
                            <option>Cultes & Messe</option>
                            <option>Conférences</option>
                            <option>Concerts</option>
                            <option>Formations & Enseignements</option>
                            <option>Jeunesse & Activités</option>
                            <option>Prières</option>
                            <option>Spécial</option>
                            <option>Autre</option>
                          </select>
                        </div>
                      </>
                    )}
                
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-navy">
                        Présentez-vous brièvement *
                        <span className="ml-1 font-normal text-gray-400">({charCount}/500)</span>
                      </label>
                      <textarea
                        name="bio"
                        required
                        maxLength={500}
                        rows={3}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        placeholder={
                          selectedRole === 'vendor'
                            ? 'Décrivez vos produits, votre expérience, pourquoi vous souhaitez rejoindre Foi Senegal...'
                            : 'Présentez votre organisation, vos événements passés, pourquoi rejoindre Foi Senegal...'
                        }
                        className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm transition-all focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/40"
                      ></textarea>
                    </div>
                
                    {error && (
                      <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                        <i className="ri-error-warning-line flex-shrink-0"></i>
                        <span>{error}</span>
                      </div>
                    )}
                
                    <button
                      type="submit"
                      disabled={!selectedRole || loading}
                      className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-navy px-4 py-3.5 text-center font-bold text-white transition-all hover:bg-navy-light disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          Envoyer ma demande d'inscription
                        </>
                      )}
                    </button>
                
                    <p className="text-center text-xs text-gray-400">
                      En soumettant ce formulaire, vous acceptez que vos informations soient examinées par notre équipe de modération.
                    </p>
                  </form>
                
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-400">
                      Déjà un compte ?{' '}
                      <Link to="/login" className="font-semibold text-navy hover:underline">
                        Se connecter
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
